/* Forge3DLab store landing — cinematic interactions (vanilla). */
(() => {
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* ---- Hero title split into staggered words (called by applyLang) ---- */
  const heroTitle = document.querySelector("[data-split]");
  const splitHeroTitle = () => {
    if (!heroTitle) return;
    const words = (heroTitle.textContent || "").trim().split(/\s+/);
    heroTitle.textContent = "";
    words.forEach((w, i) => {
      const span = document.createElement("span");
      span.className = "word";
      span.style.setProperty("--wd", `${i * 90}ms`);
      span.textContent = w;
      heroTitle.appendChild(span);
    });
  };

  /* ---- Seamless marquee: duplicate the track content ------------------ */
  for (const id of ["announceTrack", "heroTrustTrack", "offerTrustTrack", "finalTrustTrack"]) {
    const track = document.getElementById(id);
    if (track) track.innerHTML += track.innerHTML;
  }

  /* ---- Sticky buy bar --------------------------------------------------
     Shown once the offer section has scrolled fully above the fold, hidden
     again over the closing CTA so it never sits on top of that button.
     Driven from the scroll handler below rather than an IntersectionObserver:
     IO reports the rect from the moment the threshold was crossed, and an
     instant jump (anchor link, scrollIntoView) leaves that rect describing a
     position the page has already left. */
  const buyBar = document.getElementById("buyBar");
  const offerSec = document.getElementById("offer");
  const finalSec = document.getElementById("final-cta");
  const syncBuyBar = () => {
    if (!buyBar || !offerSec) return;
    const pastOffer = offerSec.getBoundingClientRect().bottom <= 0;
    const atEnd = finalSec ? finalSec.getBoundingClientRect().top < window.innerHeight : false;
    const show = pastOffer && !atEnd;
    buyBar.hidden = false;                 // the attribute only guards the pre-JS frame
    buyBar.classList.toggle("on", show);
    buyBar.setAttribute("aria-hidden", show ? "false" : "true");
  };

  /* ---- Topbar scrolled state + scroll progress + parallax ------------- */
  const topbar = document.getElementById("topbar");
  const progress = document.getElementById("progress");
  const parallax = reduce ? [] : document.querySelectorAll(".parallax");
  /* Runs at most once per frame. The scroll event can fire more often than the
     display refreshes, and the body of this handler reads layout
     (scrollHeight, getBoundingClientRect) right after writing styles, which
     forces a synchronous re-layout every time it runs. Coalescing into one
     rAF caps that at one per frame. */
  const update = () => {
    const y = window.scrollY;
    if (topbar) topbar.classList.toggle("scrolled", y > 24);
    if (progress) {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      progress.style.setProperty("--pw", `${h > 0 ? (y / h) * 100 : 0}%`);
    }
    // read every rect first, then write — interleaving them thrashes layout
    const measured = [];
    for (const el of parallax) {
      const rect = el.getBoundingClientRect();
      const speed = parseFloat(el.dataset.speed || "0.2");
      measured.push([el, (rect.top + rect.height / 2 - window.innerHeight / 2) * -speed]);
    }
    for (const [el, offset] of measured) el.style.setProperty("--py", `${offset.toFixed(1)}px`);
    syncBuyBar();
  };
  let ticking = false;
  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => { ticking = false; update(); });
  };
  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", syncBuyBar);
  syncBuyBar();
  update();

  /* ---- Language + currency selector (i18n) ---------------------------- */
  const lang = document.getElementById("lang");
  const langBtn = document.getElementById("langBtn");
  const langMenu = document.getElementById("langMenu");
  const I18N = window.I18N || {};
  const CODES = ["us", "it", "es", "de", "fr"];

  /* ---- Ad message-match: ?ang=dusty|search|earn swaps the hero copy ----
     Each Meta ad links with its angle param so the headline repeats the ad
     hook exactly (CRO leverage #1 on cold traffic). ?lang=us|it|es|de|fr
     forces the landing language to match the ad language. No param = the
     default value-stack hero. */
  const QS = (() => {
    try { return new URLSearchParams(window.location.search); } catch (e) { return null; }
  })();
  const ANGLE = (() => {
    const a = ((QS && QS.get("ang")) || "").toLowerCase();
    return ["dusty", "search", "earn"].includes(a) ? a : null;
  })();
  if (ANGLE) {
    for (const code of Object.keys(I18N)) {
      const d = I18N[code];
      for (const k of ["hero.title", "hero.lead", "hero.ctaGet"]) {
        if (d[k + "." + ANGLE] != null) d[k] = d[k + "." + ANGLE];
      }
    }
  }
  const setCurrency = (mode) => {
    document.querySelectorAll(".px").forEach((el) => {
      if (el.dataset[mode]) el.textContent = el.dataset[mode];
    });
  };
  const applyLang = (code) => {
    const dict = I18N[code] || I18N.us || {};
    document.querySelectorAll("[data-i18n]").forEach((el) => {
      const v = dict[el.getAttribute("data-i18n")];
      if (v != null) el.textContent = v;
    });
    document.querySelectorAll("[data-i18n-html]").forEach((el) => {
      const v = dict[el.getAttribute("data-i18n-html")];
      if (v != null) el.innerHTML = v;
    });
    splitHeroTitle();
    document.documentElement.lang = code === "us" ? "en" : code;
  };
  const selectLang = (b, persist = true) => {
    if (!b) return;
    langMenu.querySelectorAll("button").forEach((x) => x.classList.remove("sel"));
    b.classList.add("sel");
    langBtn.querySelector(".flag").textContent = b.dataset.flag;
    langBtn.querySelector(".lang-txt").textContent = b.dataset.name;
    langBtn.querySelector(".lang-cur").textContent = b.dataset.cur;
    setCurrency(b.dataset.mode);
    applyLang(b.dataset.lang);
    if (persist) { try { localStorage.setItem("f3d.lang", b.dataset.lang); } catch (e) {} }
  };
  if (lang && langBtn && langMenu) {
    const closeLang = () => { lang.classList.remove("open"); langBtn.setAttribute("aria-expanded", "false"); };
    langBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      const open = lang.classList.toggle("open");
      langBtn.setAttribute("aria-expanded", open ? "true" : "false");
    });
    langMenu.querySelectorAll("button").forEach((b) => {
      b.addEventListener("click", () => { selectLang(b); closeLang(); });
    });
    document.addEventListener("click", (e) => { if (!lang.contains(e.target)) closeLang(); });
    document.addEventListener("keydown", (e) => { if (e.key === "Escape") closeLang(); });

    // Initial language: a returning visitor's saved choice, otherwise DEFAULT
    // ITALIAN (it) is the default since 2026-07-31: the live campaign targets Italy
    // and the ads land here. English stays one click away in the selector, and
    // ?lang=us forces it from an ad URL.
    // NOTE: with AUTO_DETECT = false, an American visitor also lands in Italian
    // until they switch. Flip AUTO_DETECT to true to let the browser language
    // decide (Italian remains the fallback for unsupported languages).
    const AUTO_DETECT = false;
    let saved = null;
    try { saved = localStorage.getItem("f3d.lang"); } catch (e) {}
    // ?lang= from the ad URL wins over everything (message match: an Italian
    // ad must land on the Italian hero). "en" is accepted as an alias of "us".
    let urlLang = ((QS && QS.get("lang")) || "").toLowerCase();
    if (urlLang === "en") urlLang = "us";
    if (!CODES.includes(urlLang)) urlLang = null;
    let initCode = "it";
    if (urlLang) {
      initCode = urlLang;
    } else if (saved && CODES.includes(saved)) {
      initCode = saved;
    } else if (AUTO_DETECT) {
      const navLang = (navigator.language || "en").slice(0, 2).toLowerCase();
      initCode = CODES.includes(navLang) ? navLang : "it";
    }
    const initBtn = langMenu.querySelector('button[data-lang="' + initCode + '"]')
      || langMenu.querySelector('button[data-lang="it"]');
    // Persist the ad-forced language so the visitor stays in it on return.
    selectLang(initBtn, !!urlLang);
  } else {
    splitHeroTitle();
  }

  /* ---- Reveal on scroll + count-up ------------------------------------ */
  const revealables = document.querySelectorAll(".reveal, [data-panel]");
  const runCount = (el) => {
    if (el.dataset.done) return;
    el.dataset.done = "1";
    const target = parseInt(el.dataset.count, 10);
    const suffix = el.dataset.suffix || "";
    if (reduce || !target) { el.textContent = target.toLocaleString() + suffix; return; }
    const dur = 1400, start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      el.textContent = Math.round(target * eased).toLocaleString() + suffix;
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };
  if ("IntersectionObserver" in window) {
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          e.target.classList.add("in");
          (e.target.querySelectorAll?.("[data-count]") || []).forEach(runCount);
          if (e.target.matches("[data-count]")) runCount(e.target);
          io.unobserve(e.target);
        }
      }
    }, { threshold: 0.14, rootMargin: "0px 0px -8% 0px" });
    revealables.forEach((el) => io.observe(el));
    document.querySelectorAll("[data-count]").forEach((el) => io.observe(el));
  } else {
    revealables.forEach((el) => el.classList.add("in"));
  }

  /* ---- Pause the endless animations while they are off screen ---------
     CSS animations keep running whether or not the element is visible, so
     the four marquees and the levitating price were driving the compositor
     for the whole length of the page. On iOS that steady load is what makes
     scrolling stall for a second or two every so often. */
  if ("IntersectionObserver" in window) {
    const loopers = document.querySelectorAll(
      ".announce-track, .hero-marquee-track, .libcol-track, .price-card.featured .amt, .price-card.featured .price-tag, .float, .hero-img-zoom"
    );
    if (loopers.length) {
      const pauser = new IntersectionObserver((entries) => {
        for (const e of entries) e.target.style.animationPlayState = e.isIntersecting ? "running" : "paused";
      }, { rootMargin: "150px 0px" });
      loopers.forEach((el) => {
        el.style.animationPlayState = "paused";
        pauser.observe(el);
      });
    }
  }

  /* ---- Offer gallery: keep the thumbs and the scroll strip in sync -----
     The strip scrolls natively (CSS scroll-snap), so swipe and keyboard work
     without this block; it only paints the active thumb and jumps on click. */
  const pgView = document.getElementById("pgViewport");
  const pgThumbs = document.getElementById("pgThumbs");
  if (pgView && pgThumbs) {
    const shots = Array.from(pgView.children);
    const tabs = Array.from(pgThumbs.children);
    const paint = (i) => tabs.forEach((t, n) => t.classList.toggle("is-on", n === i));
    tabs.forEach((t, i) => {
      t.addEventListener("click", () => {
        pgView.scrollTo({ left: shots[i].offsetLeft - shots[0].offsetLeft, behavior: reduce ? "auto" : "smooth" });
        paint(i);
      });
    });
    let ticking = false;
    pgView.addEventListener("scroll", () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        const w = pgView.clientWidth || 1;
        paint(Math.max(0, Math.min(shots.length - 1, Math.round(pgView.scrollLeft / w))));
      });
    }, { passive: true });
  }

  /* ---- Card tilt + spotlight ------------------------------------------ */
  if (!reduce && window.matchMedia("(pointer:fine)").matches) {
    document.querySelectorAll("[data-tilt]").forEach((card) => {
      card.addEventListener("mousemove", (ev) => {
        const r = card.getBoundingClientRect();
        const px = (ev.clientX - r.left) / r.width, py = (ev.clientY - r.top) / r.height;
        card.style.setProperty("--ry", `${(px - 0.5) * 10}deg`);
        card.style.setProperty("--rx", `${(0.5 - py) * 10}deg`);
        card.style.setProperty("--mx", `${px * 100}%`);
        card.style.setProperty("--my", `${py * 100}%`);
        card.classList.add("tilted");
      });
      card.addEventListener("mouseleave", () => {
        card.style.setProperty("--ry", "0deg");
        card.style.setProperty("--rx", "0deg");
        card.classList.remove("tilted");
      });
    });
  }

  /* ---- Drag-to-swipe, shared by every slider on the page --------------
     The sliders move by different properties (the review tracks translate, the
     inside track uses margin-left because a transform there would break the
     hold-to-zoom), so the helper is told how to read and write the offset
     rather than assuming one.

     The first 8px of movement lock the axis: past that, a vertical gesture is
     handed back to the page and never touched again, and a horizontal one is
     captured. Without the lock a diagonal flick on a phone either scrolls and
     drags at the same time or swallows the page scroll. */
  const attachDrag = (surface, opts) => {
    if (!surface) return;
    let pid = null, x0 = 0, y0 = 0, base = 0, axis = null, moved = 0, lastMoved = 0;

    surface.addEventListener("pointerdown", (e) => {
      // cleared before the guards on purpose: a tap that lands on a link returns
      // early, and a leftover value would swallow that tap's own click. On touch a
      // swipe often produces no trailing click at all, so nothing else clears it.
      lastMoved = 0;
      if (e.pointerType === "mouse" && e.button !== 0) return;
      if (e.target.closest && e.target.closest("a, button")) return; // let controls be controls
      pid = e.pointerId; x0 = e.clientX; y0 = e.clientY;
      base = opts.get(); axis = null; moved = 0;
    });

    surface.addEventListener("pointermove", (e) => {
      if (pid !== e.pointerId) return;
      const dx = e.clientX - x0, dy = e.clientY - y0;
      if (!axis) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        axis = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
        if (axis !== "x") { pid = null; return; }        // it is a page scroll — hands off
        try { surface.setPointerCapture(e.pointerId); } catch (err) {}
        if (opts.onStart) opts.onStart();
        surface.classList.add("dragging");
      }
      moved = dx;
      opts.set(base + dx);
    }, { passive: true });

    const finish = (e) => {
      if (pid !== e.pointerId) return;
      pid = null;
      if (axis === "x") {
        surface.classList.remove("dragging");
        lastMoved = moved;
        // a flick past ~18% of a card counts as a page turn, with a 40px floor
        const threshold = Math.max(40, opts.step() * 0.18);
        opts.commit(moved <= -threshold ? 1 : moved >= threshold ? -1 : 0);
      }
      axis = null; moved = 0;
    };
    surface.addEventListener("pointerup", finish);
    surface.addEventListener("pointercancel", finish);

    // the click that trails a drag must not open a link or zoom a card
    surface.addEventListener("click", (e) => {
      if (Math.abs(lastMoved) > 6) { e.stopPropagation(); e.preventDefault(); }
      lastMoved = 0;
    }, true);
  };

  /* ---- Inside carousel: arrows + dots, no autoplay --------------------
     Deliberately not autoplaying: these three cards are read, not glanced at,
     and a slide moving under the reader is worse than no motion at all. */
  const insTrack = document.getElementById("insideTrack");
  if (insTrack) {
    const cards = Array.from(insTrack.children);
    const dots = document.getElementById("insideDots");
    const perView = () => (window.innerWidth <= 900 ? 1 : 2);
    const maxIdx = () => Math.max(0, cards.length - perView());
    let at = 0;
    const apply = () => {
      at = Math.min(at, maxIdx());
      // offsetLeft is measured against the offset parent, and margin-left shifts
      // that too — so take the card-to-card distance, which is margin-independent
      insTrack.style.marginLeft = `${-(cards[at].offsetLeft - cards[0].offsetLeft)}px`;
      if (dots) [...dots.children].forEach((d, i) => d.classList.toggle("on", i === at));
    };
    const go = (i) => { const m = maxIdx(); at = i > m ? 0 : i < 0 ? m : i; apply(); };
    const buildDots = () => {
      if (!dots) return;
      dots.innerHTML = "";
      for (let i = 0; i <= maxIdx(); i++) {
        const b = document.createElement("button");
        b.className = "rev-dot"; b.type = "button";
        b.setAttribute("aria-label", `Go to card ${i + 1}`);
        b.addEventListener("click", () => go(i));
        dots.appendChild(b);
      }
    };
    const prev = document.querySelector(".ins-prev");
    const next = document.querySelector(".ins-next");
    if (prev) prev.addEventListener("click", () => go(at - 1));
    if (next) next.addEventListener("click", () => go(at + 1));
    let rz = 0;
    window.addEventListener("resize", () => {
      clearTimeout(rz);
      rz = setTimeout(() => { buildDots(); apply(); }, 150);
    });
    const offsetOf = (i) => -(cards[i].offsetLeft - cards[0].offsetLeft);
    attachDrag(insTrack.parentElement, {
      get: () => parseFloat(insTrack.style.marginLeft) || 0,
      set: (v) => { insTrack.style.marginLeft = `${v}px`; },
      step: () => cards[0].getBoundingClientRect().width,
      commit: (dir) => { if (dir) go(at + dir); else apply(); },
    });
    buildDots();
    apply();
  }

  /* ---- Reviews carousels: autoplay + manual arrows + dots -------------
     Written for one slider originally: the arrows came from a document-wide
     querySelector and the dots from a fixed id, so a second carousel on the
     page would have driven the first one's buttons. Now each .rev-track is
     initialised on its own, with the buttons scoped to its own .rev-carousel
     and the dots container named by data-dots. */
  const initReviewSlider = (track) => {
    const slides = Array.from(track.children);
    const carousel = track.closest(".rev-carousel");
    if (!carousel || !slides.length) return;
    const prevBtn = carousel.querySelector(".rev-prev");
    const nextBtn = carousel.querySelector(".rev-next");
    const dotsWrap = track.dataset.dots ? document.getElementById(track.dataset.dots) : null;
    const perView = () => { const w = window.innerWidth; return w <= 560 ? 1 : w <= 900 ? 2 : 3; };
    const maxIdx = () => Math.max(0, slides.length - perView());
    let idx = 0, timer = null;

    let hovering = false;
    const apply = () => {
      idx = Math.min(idx, maxIdx());
      track.style.transform = `translateX(${-slides[idx].offsetLeft}px)`;
      if (dotsWrap) [...dotsWrap.children].forEach((d, i) => d.classList.toggle("on", i === idx));
    };
    const go = (i) => { const m = maxIdx(); idx = i > m ? 0 : i < 0 ? m : i; apply(); };

    // autoplay never resumes while the pointer is over the carousel (so arrow
    // clicks during hover don't silently restart it)
    const start = () => { if (!reduce && !hovering && !timer) timer = setInterval(() => go(idx + 1), 4200); };
    const stop = () => { clearInterval(timer); timer = null; };
    const restart = () => { stop(); start(); };

    const buildDots = () => {
      if (!dotsWrap) return;
      dotsWrap.innerHTML = "";
      for (let i = 0; i <= maxIdx(); i++) {
        const b = document.createElement("button");
        b.className = "rev-dot"; b.type = "button";
        b.setAttribute("aria-label", "Go to review " + (i + 1));
        b.addEventListener("click", () => { go(i); restart(); });
        dotsWrap.appendChild(b);
      }
    };

    if (nextBtn) nextBtn.addEventListener("click", () => { go(idx + 1); restart(); });
    if (prevBtn) prevBtn.addEventListener("click", () => { go(idx - 1); restart(); });

    carousel.addEventListener("pointerenter", () => { hovering = true; stop(); });
    carousel.addEventListener("pointerleave", () => { hovering = false; start(); });
    document.addEventListener("visibilitychange", () => { if (document.hidden) stop(); else start(); });
    window.addEventListener("resize", () => { buildDots(); apply(); });

    attachDrag(track.parentElement, {
      get: () => -slides[idx].offsetLeft,
      set: (v) => { track.style.transform = `translateX(${v}px)`; },
      step: () => slides[0].getBoundingClientRect().width,
      onStart: stop,
      commit: (dir) => { if (dir) go(idx + dir); else apply(); restart(); },
    });
    buildDots();
    apply();
    start();
  };
  document.querySelectorAll(".rev-track").forEach(initReviewSlider);

  /* ---- Category tiles: quick in-place reveal ---------------------------
     Touch: single tap toggles contents. Desktop: hover peeks (CSS) and a
     click zooms (handled by the hold-to-zoom module below). */
  const catTiles = document.querySelectorAll("[data-cat]");
  if (catTiles.length && !window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
    const clearAll = (except) =>
      catTiles.forEach((t) => { if (t !== except) t.classList.remove("revealed"); });
    catTiles.forEach((tile) => {
      tile.addEventListener("click", () => {
        const open = tile.classList.contains("revealed");
        clearAll(tile);          // close any other open tile
        tile.classList.toggle("revealed", !open); // tap again to close
      });
    });
    // tap outside any tile hides the revealed contents
    document.addEventListener("click", (e) => {
      if (!e.target.closest("[data-cat]")) clearAll();
    });
  }

  /* ---- Buy buttons ----------------------------------------------------
     Shopify theme build: [data-buy] links carry data-variants ("id:qty,…").
     Click = AJAX add-to-cart, then land on the theme /cart page so the
     Essential Upsell app popup fires on the checkout click (same workflow
     as the live theme). The href is a no-JS GET /cart/add fallback.
     Localhost/Vercel placeholder (href="#") keeps the demo alert. */
  const cartBadge = document.getElementById("cartBadge");
  let cartCount = 0;
  const onShopify = !!document.querySelector("[data-buy][data-variants]");

  /* Which Shopify locale should the cart and checkout run in.
     The landing picks its own language client-side, so a visitor can be
     reading Italian while sitting on the English root — and the checkout
     follows the URL, not the landing. Sending them to /it/cart keeps the
     two in step without changing where the ads point.
     Only `it` is published alongside the primary `en`; every other landing
     language falls back to the root. */
  const SHOPIFY_LOCALES = { it: "/it/" };
  const cartRoot = () => {
    const themeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    if (themeRoot !== "/") return themeRoot;        // already on a localized path — stay there
    return SHOPIFY_LOCALES[document.documentElement.lang] || "/";
  };
  /* prefix a root-relative theme path with the active locale */
  const localized = (path) => cartRoot() + String(path).replace(/^\//, "");
  document.querySelectorAll("[data-buy]").forEach((b) => {
    const variants = (b.dataset.variants || "")
      .split(",").filter(Boolean)
      .map((p) => { const [id, q] = p.split(":"); return { id: Number(id), quantity: Number(q || 1) }; });
    if (variants.length) {
      b.addEventListener("click", (e) => {
        e.preventDefault();
        fetch(localized("/cart/add.js"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ items: variants }),
        })
          .then((r) => { if (!r.ok) throw new Error(r.status); window.location.href = localized("/cart"); })
          .catch(() => { window.location.href = localized(b.getAttribute("href")); });
      });
      return;
    }
    if (b.getAttribute("href") !== "#") return;
    b.addEventListener("click", (e) => {
      e.preventDefault();
      if (cartBadge) { cartCount++; cartBadge.textContent = cartCount; }
      alert(`[DEMO localhost] "${b.dataset.buy}" → in Fase 2 apre il checkout Shopify (pixel Meta nativo).`);
    });
  });

  /* The topbar cart icon points at /cart too — same locale treatment, so a
     visitor who reaches the cart from the header lands on the same checkout
     language as one who used a buy button. */
  const cartLink = document.querySelector(".cart-btn");
  if (onShopify && cartLink && (cartLink.getAttribute("href") || "").indexOf("/cart") === 0) {
    cartLink.addEventListener("click", (e) => {
      e.preventDefault();
      window.location.href = localized(cartLink.getAttribute("href"));
    });
  }

  /* Real cart count in the topbar badge (Shopify only) */
  if (onShopify && cartBadge) {
    fetch("/cart.js")
      .then((r) => r.json())
      .then((c) => { cartBadge.textContent = c.item_count; })
      .catch(() => {});
  }
})();
